import {
  Badge,
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { useMemo, useState } from "react"

import { OpenAPI } from "../../../client"

export const Route = createFileRoute("/_layout/admin/customers")({
  component: CustomersAdminPage,
})

type CustomerItem = {
  id: number
  customer_id: string
  code: string
  name: string
  email: string
  phone: string
  country_code: string
  status: string
}

type ListResponse = {
  items: CustomerItem[]
  total: number
  limit: number
  offset: number
}

type UpsertPayload = {
  customer_id: string
  code: string
  name: string
  email: string
  phone: string
  country_code: string
  status: string
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem("access_token") || ""
  const res = await fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  })
  if (!res.ok) {
    const text = await res.text()
    try {
      const parsed = JSON.parse(text)
      throw new Error(
        parsed?.error ||
          parsed?.detail ||
          parsed?.message ||
          `HTTP ${res.status}`,
      )
    } catch {
      throw new Error(text || `HTTP ${res.status}`)
    }
  }
  return (await res.json()) as T
}

function getCustomerStatusColor(status?: string) {
  switch (status) {
    case "active":
      return "green"
    case "suspended":
      return "yellow"
    case "closed":
      return "red"
    default:
      return "gray"
  }
}

function CustomersAdminPage() {
  const navigate = useNavigate()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const isIndex = pathname === "/admin/customers"
  const createModal = useDisclosure()
  const toast = useToast()
  const queryClient = useQueryClient()
  const panelBg = useColorModeValue("white", "gray.800")
  const mutedTextColor = useColorModeValue("gray.500", "gray.300")
  const tableHeadBg = useColorModeValue("gray.50", "gray.700")
  const tableRowHoverBg = useColorModeValue("gray.50", "gray.700")
  const [q, setQ] = useState("")
  const [status, setStatus] = useState("")
  const [createForm, setCreateForm] = useState<UpsertPayload>({
    customer_id: "",
    code: "",
    name: "",
    email: "",
    phone: "",
    country_code: "",
    status: "active",
  })
  const [validationError, setValidationError] = useState<string | null>(null)

  const queryString = useMemo(() => {
    const p = new URLSearchParams()
    p.set("limit", "200")
    if (q.trim()) p.set("q", q.trim())
    if (status) p.set("status", status)
    return p.toString()
  }, [q, status])

  const listQuery = useQuery({
    queryKey: ["lms", "customers", queryString],
    queryFn: () => apiFetch<ListResponse>(`/v1/lms/customers?${queryString}`),
  })

  const activeCount = useMemo(
    () =>
      (listQuery.data?.items ?? []).filter((item) => item.status === "active")
        .length,
    [listQuery.data?.items],
  )

  const upsertMutation = useMutation({
    mutationFn: (payload: UpsertPayload) =>
      apiFetch<CustomerItem>("/v1/lms/customers", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      toast({
        title: "Klient został zapisany",
        status: "success",
        duration: 2500,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "customers"] })
      setCreateForm({
        customer_id: "",
        code: "",
        name: "",
        email: "",
        phone: "",
        country_code: "",
        status: "active",
      })
      createModal.onClose()
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się zapisać",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (customerId: string) =>
      apiFetch<{ status: string }>(`/v1/lms/customers/${customerId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast({
        title: "Klient został usunięty",
        status: "warning",
        duration: 2500,
      })
      queryClient.invalidateQueries({ queryKey: ["lms", "customers"] })
    },
    onError: (err: any) => {
      toast({
        title: "Nie udało się usunąć",
        description: err?.message ?? "Error",
        status: "error",
        duration: 4000,
      })
    },
  })

  return isIndex ? (
    <Container maxW="9xl" py={4}>
      <VStack align="stretch" spacing={4}>
        <Box borderWidth="1px" borderRadius="xl" p={5} bg={panelBg}>
          <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
            <Box>
              <Heading size="lg">Rejestr klientów</Heading>
              <Text color={mutedTextColor} mt={1}>
                Rejestr CRM klientów powiązanych z licencjami.
              </Text>
            </Box>
            <Button colorScheme="blue" onClick={createModal.onOpen}>
              Create customer
            </Button>
          </HStack>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={3}
            mt={4}
          >
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Załadowani klienci</StatLabel>
                <StatNumber>{listQuery.data?.total ?? 0}</StatNumber>
                <StatHelpText>W zakresie bieżącego filtra</StatHelpText>
              </Stat>
            </Box>
            <Box borderWidth="1px" borderRadius="lg" p={3}>
              <Stat>
                <StatLabel>Aktywni klienci</StatLabel>
                <StatNumber color={activeCount > 0 ? "green.500" : "gray.700"}>
                  {activeCount}
                </StatNumber>
                <StatHelpText>Gotowi do operacji licencyjnych</StatHelpText>
              </Stat>
            </Box>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" p={4}>
          <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={3}>
            <GridItem>
              <FormControl>
                <FormLabel>Szukaj</FormLabel>
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="kod / nazwa / e-mail"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Wszystkie</option>
                  <option value="active">aktywny</option>
                  <option value="suspended">zawieszony</option>
                  <option value="closed">zamknięty</option>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem alignSelf="end">
              <Button
                w="full"
                onClick={() =>
                  queryClient.invalidateQueries({
                    queryKey: ["lms", "customers"],
                  })
                }
              >
                Odśwież listę
              </Button>
            </GridItem>
          </Grid>
        </Box>

        <Box borderWidth="1px" borderRadius="xl" overflow="hidden">
          <TableContainer>
            <Table size="sm">
              <Thead bg={tableHeadBg}>
                <Tr>
                  <Th>ID klienta</Th>
                  <Th>Kod</Th>
                  <Th>Nazwa</Th>
                  <Th>Status</Th>
                  <Th>E-mail</Th>
                  <Th />
                </Tr>
              </Thead>
              <Tbody>
                {listQuery.isLoading ? (
                  <Tr>
                    <Td colSpan={6}>
                      <Spinner size="sm" />
                    </Td>
                  </Tr>
                ) : (listQuery.data?.items ?? []).length === 0 ? (
                  <Tr>
                    <Td
                      colSpan={6}
                      textAlign="center"
                      py={6}
                      color={mutedTextColor}
                    >
                      Brak klientów dla bieżących filtrów.
                    </Td>
                  </Tr>
                ) : (
                  (listQuery.data?.items ?? []).map((c) => (
                    <Tr
                      key={c.id}
                      _hover={{ bg: tableRowHoverBg }}
                      cursor="pointer"
                      onClick={() =>
                        navigate({
                          to: "/admin/customers/$customerId",
                          params: { customerId: c.customer_id },
                        })
                      }
                    >
                      <Td fontFamily="mono" fontSize="xs">
                        {c.customer_id}
                      </Td>
                      <Td>{c.code}</Td>
                      <Td>{c.name}</Td>
                      <Td>
                        <Badge colorScheme={getCustomerStatusColor(c.status)}>
                          {c.status}
                        </Badge>
                      </Td>
                      <Td>{c.email || "-"}</Td>
                      <Td>
                        <HStack>
                          <Button
                            size="xs"
                            onClick={(event) => {
                              event.stopPropagation()
                              navigate({
                                to: "/admin/customers/$customerId",
                                params: { customerId: c.customer_id },
                              })
                            }}
                          >
                            Edytuj
                          </Button>
                          <Button
                            size="xs"
                            colorScheme="red"
                            variant="outline"
                            isLoading={deleteMutation.isPending}
                            onClick={(event) => {
                              event.stopPropagation()
                              deleteMutation.mutate(c.customer_id)
                            }}
                          >
                            Usuń
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </VStack>
      <Modal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        size="5xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Utwórz klienta</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {validationError ? (
              <Box mb={3} color="red.500" fontSize="sm">
                {validationError}
              </Box>
            ) : null}
            <Grid
              templateColumns={{
                base: "1fr",
                md: "repeat(12, minmax(0, 1fr))",
              }}
              gap={3}
            >
              <GridItem colSpan={{ base: 1, md: 4 }}>
                <FormControl>
                  <FormLabel mb={1} fontSize="sm">
                    Kod
                  </FormLabel>
                  <Input
                    placeholder="kod"
                    value={createForm.code}
                    onChange={(e) =>
                      setCreateForm((s) => ({ ...s, code: e.target.value }))
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 4 }}>
                <FormControl>
                  <FormLabel mb={1} fontSize="sm">
                    Status
                  </FormLabel>
                  <Select
                    value={createForm.status}
                    onChange={(e) =>
                      setCreateForm((s) => ({ ...s, status: e.target.value }))
                    }
                  >
                    <option value="active">aktywny</option>
                    <option value="suspended">zawieszony</option>
                    <option value="closed">zamknięty</option>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 4 }}>
                <FormControl>
                  <FormLabel mb={1} fontSize="sm">
                    Nazwa
                  </FormLabel>
                  <Input
                    placeholder="nazwa"
                    value={createForm.name}
                    onChange={(e) =>
                      setCreateForm((s) => ({ ...s, name: e.target.value }))
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 4 }}>
                <FormControl>
                  <FormLabel mb={1} fontSize="sm">
                    E-mail
                  </FormLabel>
                  <Input
                    placeholder="e-mail"
                    value={createForm.email}
                    onChange={(e) =>
                      setCreateForm((s) => ({ ...s, email: e.target.value }))
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl>
                  <FormLabel mb={1} fontSize="sm">
                    Telefon
                  </FormLabel>
                  <Input
                    placeholder="telefon"
                    value={createForm.phone}
                    onChange={(e) =>
                      setCreateForm((s) => ({ ...s, phone: e.target.value }))
                    }
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl>
                  <FormLabel mb={1} fontSize="sm">
                    Kraj
                  </FormLabel>
                  <Input
                    placeholder="kod kraju"
                    value={createForm.country_code}
                    onChange={(e) =>
                      setCreateForm((s) => ({
                        ...s,
                        country_code: e.target.value,
                      }))
                    }
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                variant="outline"
                onClick={() => {
                  createModal.onClose()
                  setValidationError(null)
                }}
              >
                Anuluj
              </Button>
              <Button
                colorScheme="blue"
                isLoading={upsertMutation.isPending}
                onClick={() => {
                  setValidationError(null)
                  upsertMutation.mutate(createForm)
                }}
              >
                Zapisz
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  ) : (
    <Outlet />
  )
}
